import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageSquare, Search, X, ChevronDown, CheckCheck } from 'lucide-react'
import { ConfirmModal } from '../common/ConfirmModal'
import { useCommentList, useCommentSearch, useResolveComment, useDeleteComment, useBatchResolveComments } from '../../hooks/useComments'
import { addReaction, removeReaction } from '../../services/commentsApi'
import { useNegotiationSSE } from '../../hooks/useNegotiationSSE'
import { scrollToComment, readCommentControls, syncMissingContentControls } from '../../hooks/useCommentControls'
import { useUnreadComments } from '../../hooks/useUnreadComments'
import { CommentItem } from './CommentItem'
import { CommentCompose } from './CommentCompose'
import { BatchClassifyBanner } from './BatchClassifyBanner'
import { TypingIndicator } from './TypingIndicator'
import { SkeletonComment } from '../common/Skeleton'
import { EmptyState } from '../common/EmptyState'
import type { NegotiationComment, NegotiationParty } from '../../services/negotiationsApi'
import { devLog } from '../../services/devLog'
import { getReplyVisibility } from './replyVisibility'

interface CommentPanelProps {
  negotiationId: number
  currentVersion: number
  parties: NegotiationParty[]
  actorId?: string
  showToast?: (type: 'success' | 'error' | 'info', message: string) => void
}

type FilterMode = 'all' | 'unresolved' | 'resolved'

export function CommentPanel({
  negotiationId,
  currentVersion,
  parties,
  actorId,
  showToast,
}: CommentPanelProps) {
  const { t } = useTranslation()
  // Fetch ALL comments (no version filter) so anchors from any version are visible
  // — matches the web frontend behavior
  const { data: comments, isLoading, refetch, hasMore, loadMore } = useCommentList(negotiationId)
  const resolveComment = useResolveComment()
  const deleteComment = useDeleteComment()
  const batchResolve = useBatchResolveComments()

  // Batch resolve all mine
  const [showBatchResolveConfirm, setShowBatchResolveConfirm] = useState(false)
  const [batchResolveToast, setBatchResolveToast] = useState<string | null>(null)

  // SSE for real-time updates and typing indicator
  const { typingUsers } = useNegotiationSSE({ negotiationId })

  const [filter, setFilter] = useState<FilterMode>('unresolved')
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  // Threading state
  const [expandedThreads, setExpandedThreads] = useState<Set<number>>(new Set())
  const [replyingTo, setReplyingTo] = useState<number | null>(null)

  // Unread tracking
  const unread = useUnreadComments(negotiationId, comments, actorId)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Server-side search (used when query >= 2 chars)
  const isServerSearch = searchQuery.trim().length >= 2
  const { data: searchResults, isLoading: isSearching } = useCommentSearch(
    negotiationId,
    searchQuery,
  )

  // When comments load, sync missing Content Controls into the document
  useEffect(() => {
    if (!comments?.length) return

    readCommentControls()
      .then(async (ccs) => {
        devLog.info('CommentPanel', `Found ${ccs.length} existing CC(s) in document`)
        const existingTags = new Set(ccs.map(cc => cc.tag))

        // Find comments with anchors but no CC in document
        const needsSync = comments.filter(
          c => c.word_cc_tag && c.anchors?.length > 0 && c.anchors[0].anchor_text && !existingTags.has(c.word_cc_tag)
        )

        if (needsSync.length > 0) {
          devLog.info('CommentPanel', `Syncing ${needsSync.length} missing CC(s) into document`)
          const getColor = (c: { visibility: string }) => {
            if (c.visibility === 'INTERNAL') return '#F59E0B'
            if (c.visibility === 'ALL') return '#3B82F6'
            return '#8B5CF6'
          }
          const created = await syncMissingContentControls(needsSync, existingTags, getColor)
          devLog.info('CommentPanel', `Created ${created} CC(s) in document`)
        }
      })
      .catch(() => {
        // Reading/syncing CCs may fail if Word API is not available
      })
  }, [comments])

  // 6c: Auto-scroll to newest comment when new ones arrive via SSE
  const prevNewIdsLenRef = useRef(0)
  useEffect(() => {
    if (unread.newIds.length > prevNewIdsLenRef.current && scrollContainerRef.current) {
      // Scroll to bottom to show the new comment
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        })
      })
    }
    prevNewIdsLenRef.current = unread.newIds.length
  }, [unread.newIds])

  // Use server-side search results or local comments
  // With threaded=true, replies are nested — no need to filter parent_comment_id
  const baseComments = isServerSearch ? (searchResults ?? []) : (comments ?? [])

  // Filter comments
  const filteredComments = baseComments.filter((c) => {
    if (c.is_deleted) return false
    if (filter === 'resolved') return c.resolved
    if (filter === 'unresolved') return !c.resolved
    return true
  })

  const totalCount = (comments ?? []).filter((c) => !c.is_deleted).length
  const unresolvedCount = (comments ?? []).filter((c) => !c.is_deleted && !c.resolved).length
  const myUnresolvedCount = (comments ?? []).filter(
    (c) => !c.is_deleted && !c.resolved && c.author?.koblink_id === actorId,
  ).length
  const unclassifiedIds = (comments ?? [])
    .filter((c) => !c.is_deleted && c.needs_classification)
    .map((c) => c.id)

  const handleClick = useCallback(async (comment: NegotiationComment) => {
    const anchor = comment.anchors?.[0]
    await scrollToComment(
      comment.word_cc_tag,
      anchor?.anchor_text,
      anchor?.anchor_paragraph,
    )
  }, [])

  const handleResolve = useCallback(
    (commentId: number) => {
      resolveComment.mutate({ negotiationId, commentId })
    },
    [negotiationId, resolveComment],
  )

  const [deleteTarget, setDeleteTarget] = useState<number | null>(null)

  const handleDelete = useCallback(
    (commentId: number) => {
      setDeleteTarget(commentId)
    },
    [setDeleteTarget],
  )

  const confirmDelete = useCallback(() => {
    if (deleteTarget != null) {
      deleteComment.mutate({ negotiationId, commentId: deleteTarget })
      setDeleteTarget(null)
    }
  }, [deleteTarget, negotiationId, deleteComment, setDeleteTarget])

  const handleBatchResolveConfirm = useCallback(async () => {
    setShowBatchResolveConfirm(false)
    try {
      const result = await batchResolve.mutateAsync({
        negotiationId,
        data: { scope: 'all_mine' },
      })
      setBatchResolveToast(
        t('comments.batchResolveSuccess', '{{count}} comments resolved', { count: result.resolved_count }),
      )
      showToast?.('success', t('comments.batchResolveSuccess', '{{count}} comments resolved', { count: result.resolved_count }))
      setTimeout(() => setBatchResolveToast(null), 3000)
    } catch {
      setBatchResolveToast(t('comments.batchResolveFailed', 'Failed to resolve comments'))
      showToast?.('error', t('comments.batchResolveFailed', 'Failed to resolve comments'))
      setTimeout(() => setBatchResolveToast(null), 3000)
    }
  }, [negotiationId, batchResolve, t, showToast])

  const handleCommentCreated = useCallback(() => {
    setReplyingTo(null)
    refetch()
  }, [refetch])

  const handleToggleExpand = useCallback((commentId: number) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev)
      if (next.has(commentId)) {
        next.delete(commentId)
      } else {
        next.add(commentId)
      }
      return next
    })
  }, [])

  const scrollToFirstNew = useCallback(() => {
    if (unread.newIds.length === 0) return
    const firstNewId = Math.min(...unread.newIds)
    const el = scrollContainerRef.current?.querySelector(`#comment-${firstNewId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    unread.markAllRead()
  }, [unread])

  const handleReaction = useCallback(
    async (commentId: number, emoji: string, add: boolean) => {
      try {
        if (add) {
          await addReaction(negotiationId, commentId, emoji)
        } else {
          await removeReaction(negotiationId, commentId, emoji)
        }
        refetch()
      } catch {
        devLog.warn('CommentPanel', `Failed to ${add ? 'add' : 'remove'} reaction`)
      }
    },
    [negotiationId, refetch],
  )

  const handleReply = useCallback((commentId: number) => {
    // Expand thread and activate reply compose
    setExpandedThreads((prev) => new Set(prev).add(commentId))
    setReplyingTo(commentId)
  }, [])

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null)
  }, [])

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden border-t border-neutral-200 dark:border-neutral-700">
      {/* Header — shrink-0 keeps it fixed */}
      <div className="shrink-0 px-3 py-2 flex items-center gap-2 border-b border-neutral-100 dark:border-neutral-800">
        <MessageSquare size={14} className="text-neutral-500" />
        <span className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">
          {t('comments.title')}
        </span>
        {totalCount > 0 && (
          <span className="text-[10px] text-neutral-400">
            ({unresolvedCount}/{totalCount})
          </span>
        )}
        {unread.unreadCount > 0 && (
          <button
            onClick={() => unread.markAllRead()}
            className="px-1.5 py-0.5 rounded-full bg-primary-500/20 text-primary-600 dark:text-primary-400 text-[9px] font-medium hover:bg-primary-500/30 transition-colors"
            title={t('comments.markAllRead', 'Merk alle som lest')}
          >
            {unread.unreadCount} {t('comments.new', 'ny')}
          </button>
        )}

        {/* Filter tabs + search toggle + resolve all mine */}
        <span className="flex-1" />
        {myUnresolvedCount > 0 && (
          <button
            onClick={() => setShowBatchResolveConfirm(true)}
            disabled={batchResolve.isPending}
            className="p-0.5 rounded transition-colors text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 disabled:opacity-40"
            title={t('comments.resolveAllMine', 'Resolve all mine')}
          >
            <CheckCheck size={12} />
          </button>
        )}
        <button
          onClick={() => setShowSearch((s) => !s)}
          className={`p-0.5 rounded transition-colors ${
            showSearch
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
          }`}
        >
          <Search size={12} />
        </button>
        <div className="flex gap-0.5">
          {(['unresolved', 'all', 'resolved'] as FilterMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setFilter(mode)}
              className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${
                filter === mode
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-medium'
                  : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
              }`}
            >
              {t(`comments.filter${mode.charAt(0).toUpperCase() + mode.slice(1)}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Search bar */}
      {showSearch && (
        <div className="shrink-0 px-3 py-1.5 flex items-center gap-1.5 border-b border-neutral-100 dark:border-neutral-800">
          <Search size={12} className="text-neutral-400 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('comments.search')}
            className="flex-1 text-[11px] bg-transparent outline-none placeholder:text-neutral-500"
            autoFocus
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <X size={10} className="text-neutral-400" />
            </button>
          )}
        </div>
      )}

      {/* Batch classify banner */}
      <div className="shrink-0">
        <BatchClassifyBanner
          negotiationId={negotiationId}
          commentIds={unclassifiedIds}
          parties={parties}
        />
      </div>

      {/* Comment list */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto scroll-smooth relative">
        {/* New comments toast */}
        {unread.newIds.length > 0 && (
          <div className="sticky top-0 z-10 px-3 py-1.5 animate-toast-slide-down">
            <button
              onClick={scrollToFirstNew}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600/95 text-white text-[10px] font-medium shadow-lg backdrop-blur-sm hover:bg-primary-700 transition-colors"
            >
              <ChevronDown size={12} />
              {unread.newIds.length === 1
                ? t('comments.newComment', '1 ny kommentar')
                : t('comments.newComments', { count: unread.newIds.length })}
            </button>
          </div>
        )}

        {(isLoading || (isServerSearch && isSearching)) ? (
          <div className="py-2 space-y-1">
            <SkeletonComment />
            <SkeletonComment />
            <SkeletonComment />
          </div>
        ) : filteredComments.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title={isServerSearch ? t('comments.noSearchResults') : t('comments.emptyTitle')}
            description={isServerSearch ? undefined : t('comments.emptyDescription')}
          />
        ) : (
          filteredComments.map((comment) => (
            <div key={comment.id}>
              <CommentItem
                comment={comment}
                parties={parties}
                negotiationId={negotiationId}
                currentVersion={currentVersion}
                isAuthor={comment.author?.koblink_id === actorId}
                onResolve={handleResolve}
                onDelete={handleDelete}
                onClick={handleClick}
                onReply={handleReply}
                onReaction={handleReaction}
                isExpanded={expandedThreads.has(comment.id)}
                onToggleExpand={handleToggleExpand}
                actorId={actorId}
                isUnread={unread.isUnread(comment.id)}
                unreadReplyCount={unread.unreadReplyCountFor(comment.id)}
              />

              {/* Inline reply compose — smart reply targeting */}
              {replyingTo === comment.id && (
                <div className="pl-4 ml-3 border-l border-neutral-200 dark:border-neutral-700">
                  <CommentCompose
                    negotiationId={negotiationId}
                    currentVersion={currentVersion}
                    parties={parties}
                    actorId={actorId}
                    defaultVisibility={getReplyVisibility(comment, parties)}
                    onCommentCreated={handleCommentCreated}
                    parentCommentId={comment.id}
                    compact
                    onCancel={handleCancelReply}
                  />
                </div>
              )}
            </div>
          ))
        )}

        {/* Load more button */}
        {hasMore && !isServerSearch && (
          <button
            onClick={loadMore}
            className="w-full py-2 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
          >
            {t('comments.loadMore', 'Load more comments')}
          </button>
        )}
      </div>

      {/* Typing indicator */}
      <TypingIndicator typingUsers={typingUsers} currentActorId={actorId} />

      {/* Compose area (root comments) */}
      <CommentCompose
        negotiationId={negotiationId}
        currentVersion={currentVersion}
        parties={parties}
        actorId={actorId}
        showToast={showToast}
        onCommentCreated={handleCommentCreated}
      />

      {/* Batch resolve toast */}
      {batchResolveToast && (
        <div className="shrink-0 px-3 py-1.5 text-center border-t border-neutral-100 dark:border-neutral-800">
          <span className="text-[10px] font-medium text-accent-600 dark:text-accent-400">
            {batchResolveToast}
          </span>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        open={deleteTarget != null}
        title={t('comments.deleteConfirmTitle', 'Delete comment?')}
        description={t('comments.deleteConfirmDescription', 'This comment will be removed for all parties.')}
        confirmLabel={t('comments.delete')}
        variant="danger"
        isPending={deleteComment.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Batch resolve confirmation modal */}
      <ConfirmModal
        open={showBatchResolveConfirm}
        title={t('comments.resolveAllMineConfirmTitle', 'Resolve all your comments?')}
        description={t('comments.resolveAllMineConfirmDescription', 'All your unresolved comments in this negotiation will be marked as resolved.')}
        confirmLabel={t('comments.resolveAllMine', 'Resolve all mine')}
        variant="warning"
        isPending={batchResolve.isPending}
        onConfirm={handleBatchResolveConfirm}
        onCancel={() => setShowBatchResolveConfirm(false)}
      />
    </div>
  )
}
